import { useCallback, useEffect, useMemo, useState } from 'react'
import { ClipboardPaste, Copy, Plus, Trash2, X } from 'lucide-react'
import { erfuelltRecht } from '@symphenia/shared-types'
import { WAHL_EINTRAG, WahlFenster, useWahlTasten } from '@/components/positionswahl/PositionsWahl'
import { apiErrorText } from '@/lib/api-error'
import { formCard } from '@/lib/form-styles'
import { useAuthStore } from '@/lib/auth.store'
import { useTerminology } from '@/lib/useTerminology'
import {
  copyPositions,
  createGroup,
  deleteGroup,
  renameGroup,
  fetchClipboard,
  fetchPosition,
  fetchPositions,
  fetchProjectTrash,
  renumberPositions,
  restorePositionTrash,
  restoreWorkStep,
  type PositionDeleted,
  type ProjectTrashEntry,
} from '../api/projects-phase-d.api'
import { alsTag } from '../project-labels'
import { Kennzeile } from './Kennzeile'
import type { PositionDetail, PositionListResponse, PositionRow } from '../types/phase-d.types'
import type { ProjectDetail } from '../types/project.types'
import { PositionFormDialog } from './PositionFormDialog'
import { PositionPasteDialog } from './PositionPasteDialog'
import { PositionDetailPanel } from './PositionDetailPanel'
import { restoreBomLine } from '../api/project-bom.api'
import { restoreConsumable } from '../api/project-controlling.api'
import { bestaetige, frage } from '@/lib/dialoge'

// ── Zuletzt geöffnete Positionen (Nachtrag 7) ────────────────────────────────
// Arbeitskontext je Projekt und Browser, wie die Listenansicht der Projekte
// (`project-navigation.ts`): kein Mandanten-Standard, nichts, was der Server wissen muss.
/** Nachtrag 8a/10: die Art einer Zeile im Papierkorb des Projekts. */
const ART_KURZ: Record<Exclude<ProjectTrashEntry['kind'], 'POSITION'>, string> = { BOM_LINE: 'Stückliste', CONSUMABLE: 'Verbrauch', WORK_STEP: 'Arbeitsschritt' }
const ART_LANG: Record<Exclude<ProjectTrashEntry['kind'], 'POSITION'>, string> = { BOM_LINE: 'Stückliste', CONSUMABLE: 'Verbrauchsmaterial', WORK_STEP: 'Arbeitsschritte' }

const ZULETZT_PREFIX = 'symphenia.ui.projects.recentPositions'
const ZULETZT_MAX = 10

function ladeZuletzt(projektId: string): string[] {
  try {
    const roh = localStorage.getItem(`${ZULETZT_PREFIX}.${projektId}`)
    const liste = roh ? (JSON.parse(roh) as unknown) : []
    return Array.isArray(liste) ? liste.filter((x): x is string => typeof x === 'string') : []
  } catch {
    return []
  }
}

function merkeZuletzt(projektId: string, positionId: string): string[] {
  const neu = [positionId, ...ladeZuletzt(projektId).filter((x) => x !== positionId)].slice(0, ZULETZT_MAX)
  try {
    localStorage.setItem(`${ZULETZT_PREFIX}.${projektId}`, JSON.stringify(neu))
  } catch {
    // ignorieren — ohne Merker bleibt nur ↶ leer
  }
  return neu
}

/**
 * AP-35 Detailblatt 2 8. — **Der Reiter Positionen.**
 *
 * Die gewählte Position in ganzer Breite; die Liste mit Gruppen als Zwischenüberschriften
 * klappt an ihrer Überschrift auf (Nachtrag 15: die Liste links ist entfallen, damit die
 * Zeilenlisten der Position die Breite bekommen). Sie lädt **alle** Positionen (14.2-13) —
 * über 500 Positionen sind ein LV-Import, wo die Gruppierung greift.
 *
 * Was die Liste links sonst trug, liegt in der Aufklappliste (N15-4): Anlegen und Einfügen,
 * Markieren und Kopieren, Gruppen, Neu nummerieren, der Papierkorb des Projekts. Den
 * Statusfilter gibt es nicht mehr, „storn." bleibt (N15-5).
 *
 * **Positionen entstehen nur auf zwei Wegen:** von Hand („+ Position") oder aus
 * der Zwischenablage („Einfügen"). Es gibt kein „übernehmen aus" und keinen
 * Dialog, der einen Beleg öffnet (Auftraggeber 16.9.2026) — wer eine Zeile
 * woanders hat, kopiert sie dort und fügt sie hier ein.
 */
export function ProjectPositionsTab({ projekt, startPositionId, startEinfuegen = false }: { projekt: ProjectDetail; startPositionId?: string | null; startEinfuegen?: boolean }) {
  const terms = useTerminology()
  const { user } = useAuthStore()
  const darf = (recht: string) => erfuelltRecht(recht, user?.permissions)
  const darfPflegen = darf('projects.position.manage')
  /** Nachtrag 8a: wer eine Art wiederherstellen darf — dieselben Rechte wie beim Löschen. */
  const darfWiederherstellen: Record<ProjectTrashEntry['kind'], boolean> = {
    POSITION: darfPflegen,
    BOM_LINE: darf('projects.bom.manage'),
    CONSUMABLE: darf('projects.sales.manage'),
    WORK_STEP: darf('projects.workstep.manage'),
  }
  const darfPapierkorb = Object.values(darfWiederherstellen).some(Boolean)

  const [liste, setListe] = useState<PositionListResponse | null>(null)
  const [gewaehlt, setGewaehlt] = useState<string | null>(startPositionId ?? null)
  const [detail, setDetail] = useState<PositionDetail | null>(null)
  const [markiert, setMarkiert] = useState<Set<string>>(new Set())
  const [suche, setSuche] = useState('')
  const [mitStornierten, setMitStornierten] = useState(false)
  const [ablage, setAblage] = useState(0)
  const [anlegen, setAnlegen] = useState<'SERVICE' | 'SUPPLEMENT' | null>(null)
  const [einfuegen, setEinfuegen] = useState(startEinfuegen)
  const [fehler, setFehler] = useState<string | null>(null)
  const [hinweis, setHinweis] = useState<string | null>(null)
  const [zuletzt, setZuletzt] = useState<string[]>(() => ladeZuletzt(projekt.id))
  // Nachtrag 7: die eben gelöschte Position — für „Rückgängig" direkt nach dem Löschen.
  const [geloescht, setGeloescht] = useState<PositionDeleted | null>(null)
  const [papierkorb, setPapierkorb] = useState<ProjectTrashEntry[] | null>(null)
  /** Zählt Wiederherstellungen von Zeilen — die Reiter der Position laden dann neu. */
  const [zeilenStand, setZeilenStand] = useState(0)

  const laden = useCallback(async () => {
    try {
      const antwort = await fetchPositions(projekt.id, mitStornierten)
      setListe(antwort)
      setGewaehlt((alt) => alt ?? antwort.positions[0]?.id ?? null)
      setFehler(null)
    } catch (e) {
      setFehler(apiErrorText(e, 'Das hat nicht geklappt.'))
    }
  }, [projekt.id, mitStornierten])

  const ablageZaehlen = useCallback(async () => {
    try {
      setAblage((await fetchClipboard()).length)
    } catch {
      // Die Ablage ist Beiwerk: Ist sie nicht erreichbar, bleibt die Liste nutzbar.
      setAblage(0)
    }
  }, [])

  useEffect(() => {
    void laden()
    void ablageZaehlen()
  }, [laden, ablageZaehlen])

  useEffect(() => {
    if (gewaehlt) setZuletzt(merkeZuletzt(projekt.id, gewaehlt))
  }, [projekt.id, gewaehlt])

  useEffect(() => {
    if (!gewaehlt) {
      setDetail(null)
      return
    }
    void fetchPosition(projekt.id, gewaehlt)
      .then(setDetail)
      .catch((e) => setFehler(apiErrorText(e, 'Das hat nicht geklappt.')))
  }, [projekt.id, gewaehlt])

  const gefiltert = useMemo(() => {
    const begriff = suche.trim().toLowerCase()
    return (liste?.positions ?? []).filter((p) => {
      if (!begriff) return true
      return (
        String(p.number).includes(begriff) ||
        p.name.toLowerCase().includes(begriff) ||
        (p.longText ?? '').toLowerCase().includes(begriff)
      )
    })
  }, [liste, suche])

  /** Gruppen als Zwischenüberschriften; „ohne Gruppe" steht vor allen (Blatt 2 3.). */
  const nachGruppe = useMemo(() => {
    const gruppen = liste?.groups ?? []
    const bloecke: { id: string | null; name: string; positionen: PositionRow[] }[] = [
      { id: null, name: 'ohne Gruppe', positionen: gefiltert.filter((p) => !p.groupId) },
      ...gruppen.map((g) => ({
        id: g.id,
        name: `${g.number}. ${g.name}`,
        positionen: gefiltert.filter((p) => p.groupId === g.id),
      })),
    ]
    // Eine leere Gruppe bleibt sichtbar — sonst verschwände eine eben angelegte, bis etwas darin liegt.
    // Während gesucht wird, zeigt die Liste nur Gruppen mit Treffern.
    const sucht = suche.trim() !== ''
    return bloecke.filter((b) => b.positionen.length > 0 || (b.id !== null && !sucht))
  }, [liste, gefiltert, suche])

  /**
   * Die Reihenfolge der Liste — nach ihr blättern ‹ › an der Überschrift (N15-2). Sie hängt nicht an der
   * Suche: Die steht in der Aufklappliste und wäre beim Blättern nicht zu sehen.
   */
  const reihenfolge = useMemo(() => {
    const alle = liste?.positions ?? []
    return [...alle.filter((p) => !p.groupId), ...(liste?.groups ?? []).flatMap((g) => alle.filter((p) => p.groupId === g.id))].map((p) => p.id)
  }, [liste])

  // Nachtrag 15 (N15-2, N15-3): Blättern und Aufklappliste an der Überschrift der Position.
  const [wahlOffen, setWahlOffenRoh] = useState(false)
  const setWahlOffen = useCallback((offen: boolean) => {
    setWahlOffenRoh(offen)
    if (!offen) setSuche('')
  }, [])
  const oeffne = (id: string) => {
    setWahlOffen(false)
    setGewaehlt(id)
  }
  const stelle = gewaehlt ? reihenfolge.indexOf(gewaehlt) : -1
  // Am Ende weiter zum Anfang (wie beim Projekt, N5-4).
  const schritt = (s: 1 | -1) => {
    if (reihenfolge.length < 2) return
    const i = stelle < 0 ? (s === 1 ? 0 : reihenfolge.length - 1) : (stelle + s + reihenfolge.length) % reihenfolge.length
    oeffne(reihenfolge[i])
  }
  useWahlTasten({ aktiv: reihenfolge.length > 0, onSchritt: schritt, onOeffnen: () => setWahlOffenRoh(true) })

  const papierkorbLaden = async () => {
    try {
      setPapierkorb(await fetchProjectTrash(projekt.id))
    } catch (e) {
      setFehler(apiErrorText(e, 'Das hat nicht geklappt.'))
    }
  }

  const wiederherstellen = async (trashId: string) => {
    try {
      const r = await restorePositionTrash(projekt.id, trashId)
      setGeloescht(null)
      setFehler(null)
      setHinweis(
        r.number !== r.previousNumber
          ? `${terms.position} wiederhergestellt — als Nr. ${r.number}, weil die ${r.previousNumber} inzwischen vergeben ist`
          : `${terms.position} ${r.number} wiederhergestellt`,
      )
      await laden()
      setGewaehlt(r.positionId)
      if (papierkorb) await papierkorbLaden()
    } catch (e) {
      setFehler(apiErrorText(e, 'Das hat nicht geklappt.'))
    }
  }

  /** Nachtrag 8a: eine Stücklisten- oder Verbrauchszeile (seit Nachtrag 10 auch ein Arbeitsschritt) aus dem Papierkorb des Projekts. */
  const zeileWiederherstellen = async (e: ProjectTrashEntry) => {
    try {
      if (e.kind === 'BOM_LINE') await restoreBomLine(e.id)
      else if (e.kind === 'WORK_STEP') await restoreWorkStep(e.id)
      else await restoreConsumable(e.id)
      setFehler(null)
      setHinweis(`„${e.name}“ wiederhergestellt — ${terms.position} ${e.positionNumber ?? ''}, ${e.kind === 'POSITION' ? '' : ART_LANG[e.kind]}`)
      setZeilenStand((n) => n + 1)
      await papierkorbLaden()
    } catch (err) {
      setFehler(apiErrorText(err, 'Das hat nicht geklappt.'))
    }
  }

  const umschalten = (id: string) => {
    setMarkiert((alt) => {
      const neu = new Set(alt)
      if (neu.has(id)) neu.delete(id)
      else neu.add(id)
      return neu
    })
  }

  const kopieren = async () => {
    try {
      const eintraege = await copyPositions(projekt.id, [...markiert])
      setAblage(eintraege.length)
      setMarkiert(new Set())
      setHinweis(`${eintraege.length} Einträge in der Zwischenablage`)
    } catch (e) {
      setFehler(apiErrorText(e, 'Das hat nicht geklappt.'))
    }
  }

  const neuNummerieren = async () => {
    try {
      setListe(await renumberPositions(projekt.id))
      setHinweis('Positionen neu nummeriert')
      setFehler(null)
    } catch (e) {
      setFehler(apiErrorText(e, 'Das hat nicht geklappt.'))
    }
  }

  const gruppeUmbenennen = async (id: string, alt: string) => {
    const name = await frage('Neue Bezeichnung der Gruppe', alt.replace(/^\d+\.\s*/, ''))
    if (!name?.trim()) return
    try {
      await renameGroup(projekt.id, id, name.trim())
      await laden()
    } catch (e) {
      setFehler(apiErrorText(e, 'Das hat nicht geklappt.'))
    }
  }

  const gruppeLoeschen = async (id: string, name: string) => {
    if (!(await bestaetige(`Gruppe „${name}" auflösen? Die ${terms.positionPlural} bleiben und stehen danach ohne Gruppe.`))) return
    try {
      await deleteGroup(projekt.id, id)
      await laden()
    } catch (e) {
      setFehler(apiErrorText(e, 'Das hat nicht geklappt.'))
    }
  }

  const gruppeAnlegen = async () => {
    const name = await frage('Bezeichnung der Gruppe')
    if (!name?.trim()) return
    try {
      await createGroup(projekt.id, name.trim())
      await laden()
    } catch (e) {
      setFehler(apiErrorText(e, 'Das hat nicht geklappt.'))
    }
  }

  const anlegeKnoepfe = (
    <>
      <button
        type="button"
        className="inline-flex items-center gap-1 rounded-md bg-primary px-2 py-1 text-xs text-primary-foreground"
        onClick={() => {
          setWahlOffen(false)
          setAnlegen('SERVICE')
        }}
      >
        <Plus className="h-3.5 w-3.5" /> {terms.position}
      </button>
      <button
        type="button"
        className="rounded-md border border-border px-2 py-1 text-xs"
        onClick={() => {
          setWahlOffen(false)
          setAnlegen('SUPPLEMENT')
        }}
      >
        + Nachtrag
      </button>
      <button
        type="button"
        disabled={ablage === 0}
        className="ml-auto inline-flex items-center gap-1 rounded-md border border-border px-2 py-1 text-xs disabled:opacity-40"
        onClick={() => {
          setWahlOffen(false)
          setEinfuegen(true)
        }}
        title={ablage === 0 ? 'Die Zwischenablage ist leer.' : `${ablage} aus der Zwischenablage einfügen`}
      >
        <ClipboardPaste className="h-3.5 w-3.5" /> {ablage}
      </button>
    </>
  )
  // N15-5: Der Statusfilter ist entfallen; „storn." bleibt — stornierte wären sonst nicht mehr zu erreichen.
  const stornSchalter = (
    <label className="flex shrink-0 items-center gap-1 text-[11px] text-muted-foreground" title="Stornierte zeigen">
      <input type="checkbox" checked={mitStornierten} onChange={(e) => setMitStornierten(e.target.checked)} />
      storn.
    </label>
  )
  const papierkorbKnopf = (
    <button
      type="button"
      className="inline-flex items-center gap-1 text-xs hover:underline"
      onClick={() => {
        setWahlOffen(false)
        if (papierkorb) setPapierkorb(null)
        else void papierkorbLaden()
      }}
      aria-expanded={!!papierkorb}
      title={`Gelöschte ${terms.positionPlural} sowie Stücklisten- und Verbrauchszeilen aller ${terms.positionPlural}`}
    >
      <Trash2 className="h-3 w-3" /> Papierkorb
    </button>
  )

  /** Die Aufklappliste an der Überschrift (N15-3, N15-4) — die frühere Liste links samt allem, was sie trug. */
  const fenster = (
    <WahlFenster
      suche={suche}
      onSuche={setSuche}
      onSchliessen={() => setWahlOffen(false)}
      bezeichnung={terms.positionPlural}
      kopf={darfPflegen ? anlegeKnoepfe : undefined}
      nebenSuche={stornSchalter}
      fuss={
        <span className="flex flex-wrap items-center justify-between gap-x-3 gap-y-1">
          <span>
            {liste?.summary.count ?? 0} {terms.positionPlural} · Leistungsstand {liste?.summary.progress ?? 0} %
          </span>
          {darfPflegen && (
            <span className="flex gap-2">
              <button type="button" className="hover:underline" onClick={gruppeAnlegen}>
                + Gruppe
              </button>
              <button type="button" className="hover:underline" onClick={neuNummerieren}>
                Neu nummerieren
              </button>
            </span>
          )}
          {darfPapierkorb && papierkorbKnopf}
        </span>
      }
    >
      {nachGruppe.length === 0 && (
        <p className="text-sm text-muted-foreground">
          {suche.trim() ? `Keine ${terms.position} passt zur Suche.` : `Noch keine ${terms.positionPlural}. Lege eine an oder füge aus der Zwischenablage ein.`}
        </p>
      )}
      {nachGruppe.map((block) => (
        <div key={block.id ?? 'ohne'}>
          <p className="mb-1 flex items-center gap-2 text-xs font-medium uppercase tracking-wide text-muted-foreground">
            <span>
              {block.name} · {block.positionen.length}
            </span>
            {/* Gruppen umbenennen und auflösen (Auftraggeber 18.9.2026) — die API gab es, die Oberfläche nicht. */}
            {darfPflegen && block.id && (
              <span className="ml-auto flex gap-2 normal-case tracking-normal">
                <button type="button" className="hover:text-foreground hover:underline" onClick={() => void gruppeUmbenennen(block.id!, block.name)}>
                  umbenennen
                </button>
                <button type="button" className="hover:text-destructive hover:underline" onClick={() => void gruppeLoeschen(block.id!, block.name)}>
                  auflösen
                </button>
              </span>
            )}
          </p>
          <ul className="space-y-1">
            {block.positionen.map((p) => (
              <li key={p.id}>
                <div
                  className={`flex items-start gap-2 rounded-md border px-2 py-1.5 text-left ${
                    gewaehlt === p.id ? 'border-primary bg-accent' : 'border-transparent hover:bg-accent/50'
                  } ${p.status === 'CANCELLED' ? 'opacity-60' : ''}`}
                >
                  {darfPflegen && (
                    <input type="checkbox" className="mt-1" checked={markiert.has(p.id)} onChange={() => umschalten(p.id)} aria-label={`Position ${p.number} markieren`} />
                  )}
                  <button
                    type="button"
                    data-wahl
                    data-aktuell={gewaehlt === p.id || undefined}
                    tabIndex={-1}
                    className={`min-w-0 flex-1 rounded text-left ${WAHL_EINTRAG}`}
                    onClick={() => oeffne(p.id)}
                  >
                    <p className="truncate text-sm">
                      <span className="mr-1 font-mono text-xs text-muted-foreground">{p.number}</span>
                      <span className={`font-medium ${p.status === 'CANCELLED' ? 'line-through' : ''}`}>
                        <Kennzeile position={p} />
                      </span>
                      {p.type === 'SUPPLEMENT' && (
                        <span className="ml-1 rounded bg-amber-100 px-1 text-[10px] text-amber-800 dark:bg-amber-900 dark:text-amber-200">Nachtrag N{p.supplementNo ?? ''}</span>
                      )}
                    </p>
                    <div className="mt-1 flex items-center gap-2">
                      <div className="h-1.5 flex-1 overflow-hidden rounded-full bg-muted">
                        <div className="h-full bg-primary" style={{ width: `${Math.min(100, p.progress.percent)}%` }} />
                      </div>
                      <span className="text-[11px] text-muted-foreground">{p.progress.percent} %</span>
                      {p.nextDueAt && <span className={`text-[11px] ${p.overdue ? 'text-destructive' : 'text-muted-foreground'}`}>{alsTag(p.nextDueAt)}</span>}
                      {p.waitingForRelease && <span className="text-[11px] text-yellow-700 dark:text-yellow-400">wartet</span>}
                    </div>
                  </button>
                </div>
              </li>
            ))}
          </ul>
        </div>
      ))}
      {markiert.size > 0 && (
        <div className="flex flex-wrap items-center gap-2 border-t border-border pt-2">
          <span className="text-xs text-muted-foreground">{markiert.size} markiert</span>
          <button type="button" className="inline-flex items-center gap-1 rounded-md border border-border px-2 py-1 text-xs" onClick={kopieren}>
            <Copy className="h-3 w-3" /> Kopieren
          </button>
          <button type="button" className="rounded-md border border-border px-2 py-1 text-xs" onClick={() => setMarkiert(new Set())}>
            Auswahl aufheben
          </button>
        </div>
      )}
    </WahlFenster>
  )

  return (
    <div className="space-y-3">
      {fehler && <div className="text-sm text-destructive">{fehler}</div>}
      {hinweis && <div className="text-sm text-muted-foreground">{hinweis}</div>}
      {geloescht && (
        <div className="flex flex-wrap items-center gap-3 rounded-md border border-border bg-muted/40 px-3 py-2 text-sm">
          <span>
            {terms.position} {geloescht.number} „{geloescht.name}“ gelöscht — sie liegt 30 Tage im Papierkorb.
          </span>
          <button
            type="button"
            className="rounded-md border border-border bg-background px-2 py-0.5 text-sm font-medium hover:bg-muted"
            onClick={() => void wiederherstellen(geloescht.trashId)}
          >
            Rückgängig
          </button>
          <button type="button" className="ml-auto text-xs text-muted-foreground hover:underline" onClick={() => setGeloescht(null)}>
            ausblenden
          </button>
        </div>
      )}

      {/* Nachtrag 8a: der Papierkorb des ganzen Projekts — gelöschte Positionen (mit allem, was an ihnen
          hing), Stücklisten- und Verbrauchszeilen aller Positionen, 30 Tage. Seit Nachtrag 15 (N15-4) ein
          Bereich über der Position; geöffnet wird er im Fuß der Aufklappliste. */}
      {papierkorb && (
        <div className="rounded-md border border-border bg-muted/40 p-2" role="region" aria-label="Papierkorb">
          <p className="mb-1 flex items-center justify-between text-xs font-medium uppercase tracking-wide text-muted-foreground">
            Papierkorb · 30 Tage
            <button type="button" className="rounded p-0.5 hover:bg-muted hover:text-foreground" aria-label="Papierkorb schließen" title="schließen" onClick={() => setPapierkorb(null)}>
              <X className="h-3.5 w-3.5" />
            </button>
          </p>
          {papierkorb.length === 0 && <p className="text-xs text-muted-foreground">Leer.</p>}
          <ul className="space-y-1">
            {papierkorb.map((e) => (
              <li key={e.id} className="flex items-start gap-2 text-xs">
                <span className="w-6 shrink-0 font-mono text-muted-foreground">{e.positionNumber ?? ''}</span>
                <span className="min-w-0 flex-1">
                  <span className="block truncate text-sm">{e.name}</span>
                  <span className="text-muted-foreground">
                    {e.kind === 'POSITION' ? terms.position : ART_KURZ[e.kind]}
                    {e.kind !== 'POSITION' && e.positionName ? ` in „${e.positionName}“` : ''} · gelöscht {alsTag(e.deletedAt)}
                    {e.deletedByName ? ` von ${e.deletedByName}` : ''} · bis {alsTag(e.expiresAt)}
                  </span>
                </span>
                {darfWiederherstellen[e.kind] && (
                  <button
                    type="button"
                    className="shrink-0 rounded-md border border-border bg-background px-2 py-0.5 hover:bg-muted"
                    onClick={() => void (e.kind === 'POSITION' ? wiederherstellen(e.id) : zeileWiederherstellen(e))}
                  >
                    Wiederherstellen
                  </button>
                )}
              </li>
            ))}
          </ul>
        </div>
      )}

      {/* ── Position (8.2/8.3) — seit Nachtrag 15 in ganzer Breite ───────── */}
      {detail ? (
        <PositionDetailPanel
          zeilenStand={zeilenStand}
          projekt={projekt}
          position={detail}
          onChanged={async () => {
            await laden()
            if (gewaehlt) setDetail(await fetchPosition(projekt.id, gewaehlt))
          }}
          onDeleted={async (weg) => {
            // Weiter mit der nächsten Position der Liste, sonst der vorigen.
            const i = reihenfolge.indexOf(weg.positionId)
            const danach = reihenfolge[i + 1] ?? reihenfolge[i - 1] ?? null
            setGeloescht(weg)
            setHinweis(null)
            setGewaehlt(danach)
            setDetail(null)
            await laden()
            if (papierkorb) await papierkorbLaden()
          }}
          navigation={{
            alle: liste?.positions ?? [],
            zuletzt,
            onOeffne: setGewaehlt,
            onNeu: () => setAnlegen('SERVICE'),
            wahl: { stelle, anzahl: reihenfolge.length, schritt, offen: wahlOffen, setOffen: setWahlOffen, fenster },
          }}
        />
      ) : (
        // Ohne Position gibt es keinen Kopf und damit keine Aufklappliste — Anlegen, Einfügen und „storn." stehen hier.
        <div className={`${formCard} space-y-3 text-sm text-muted-foreground`}>
          <p>{(liste?.positions.length ?? 0) === 0 ? `Noch keine ${terms.positionPlural}. Lege eine an oder füge aus der Zwischenablage ein.` : `Keine ${terms.position} gewählt.`}</p>
          <div className="flex flex-wrap items-center gap-2">
            {darfPflegen && <span className="flex items-center gap-1">{anlegeKnoepfe}</span>}
            {stornSchalter}
            {darfPapierkorb && papierkorbKnopf}
          </div>
        </div>
      )}

      {anlegen && (
        <PositionFormDialog
          projekt={projekt}
          art={anlegen}
          onClose={() => setAnlegen(null)}
          onSaved={async (neu) => {
            setAnlegen(null)
            await laden()
            setGewaehlt(neu.id)
          }}
        />
      )}
      {einfuegen && (
        <PositionPasteDialog
          projekt={projekt}
          gruppen={liste?.groups ?? []}
          positionen={liste?.positions ?? []}
          aktuellePosition={gewaehlt}
          onClose={() => setEinfuegen(false)}
          onDone={async (meldung) => {
            setEinfuegen(false)
            setHinweis(meldung)
            await laden()
            await ablageZaehlen()
          }}
        />
      )}
    </div>
  )
}
